import { useEffect, useState } from "react";
import { api, HITLError } from "../../api/client";

// ── Stage metadata ─────────────────────────────────────────────────────────

const STAGE_COLOR: Record<string, string> = {
  triage:          "text-blue-400   bg-blue-400/10   border-blue-500/30",
  detect_panels:   "text-violet-400 bg-violet-400/10 border-violet-500/30",
  ocr_ticks:       "text-cyan-400   bg-cyan-400/10   border-cyan-500/30",
  calibrate:       "text-amber-400  bg-amber-400/10  border-amber-500/30",
  extract_points:  "text-emerald-400 bg-emerald-400/10 border-emerald-500/30",
  unknown:         "text-slate-400  bg-slate-400/10  border-slate-500/30",
};

function stageColor(stage: string) {
  return STAGE_COLOR[stage] ?? STAGE_COLOR.unknown;
}

function confBadge(conf: number | null) {
  if (conf == null) return <span className="text-slate-600">—</span>;
  const color = conf < 0.5 ? "text-red-400" : conf < 0.65 ? "text-amber-400" : "text-emerald-400";
  return <span className={`font-mono text-xs ${color}`}>{(conf * 100).toFixed(0)}%</span>;
}

function outcomeBadge(outcome: string | null) {
  if (!outcome) return <span className="text-slate-600 text-xs">pending</span>;
  const map: Record<string, string> = {
    APPROVED_NO_EDIT:    "text-emerald-400",
    APPROVED_WITH_EDITS: "text-blue-400",
    REJECTED:            "text-red-400",
    FLAGGED:             "text-amber-400",
  };
  return <span className={`text-xs font-medium ${map[outcome] ?? "text-slate-400"}`}>{outcome.replace(/_/g, " ")}</span>;
}

// ── Retraining guide ───────────────────────────────────────────────────────

function RetrainingGuide() {
  return (
    <div className="space-y-4 text-sm text-slate-300">
      <div className="flex items-center gap-2 border-b border-rdp-border pb-3">
        <span className="text-base">🚀</span>
        <h2 className="text-white font-semibold">YOLOv8 Retraining Guide</h2>
      </div>

      <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/50 border border-rdp-border">
        <span className="text-violet-400 font-bold text-xs mt-0.5">01</span>
        <div>
          <p className="font-medium text-white mb-1">Collect failed cases</p>
          <p className="text-slate-400 text-xs leading-relaxed">
            Every chart that triggers HITL review is automatically backed up in
            <code className="mx-1 px-1 rounded bg-slate-700 text-amber-300">active_learning_data/raw_images/</code>.
            Charts flagged via the pipeline are copied to
            <code className="mx-1 px-1 rounded bg-slate-700 text-amber-300">failed_cases/</code>.
          </p>
        </div>
      </div>

      <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/50 border border-rdp-border">
        <span className="text-violet-400 font-bold text-xs mt-0.5">02</span>
        <div>
          <p className="font-medium text-white mb-1">Label in Label Studio / LabelImg</p>
          <p className="text-slate-400 text-xs leading-relaxed">
            Open images from <code className="px-1 rounded bg-slate-700 text-amber-300">failed_cases/</code>,
            draw correct bounding boxes, export as <strong>YOLO format</strong>.
            Mix with at least 30–50 correct examples to prevent overfitting.
          </p>
        </div>
      </div>

      <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/50 border border-rdp-border">
        <span className="text-violet-400 font-bold text-xs mt-0.5">03</span>
        <div>
          <p className="font-medium text-white mb-1">Fine-tune on existing weights</p>
          <pre className="mt-2 p-2 rounded bg-slate-900 text-xs text-emerald-300 overflow-x-auto leading-relaxed whitespace-pre-wrap">{`from ultralytics import YOLO
model = YOLO("best.pt")   # start from current weights
model.train(
    data="active_learning_data/data.yaml",
    epochs=15,
    imgsz=640,
    lr0=0.001,   # low LR to preserve prior knowledge
    project="yolo_output",
    name="chart_model_v2",
)`}</pre>
        </div>
      </div>

      <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/50 border border-rdp-border">
        <span className="text-violet-400 font-bold text-xs mt-0.5">04</span>
        <div>
          <p className="font-medium text-white mb-1">Deploy new weights</p>
          <p className="text-slate-400 text-xs leading-relaxed">
            Copy <code className="px-1 rounded bg-slate-700 text-amber-300">yolo_output/chart_model_v2/weights/best.pt</code> to
            <code className="ml-1 px-1 rounded bg-slate-700 text-amber-300">/home/hamed/RapidDigitizerPipLine/best.pt</code> on the server,
            then restart the heavy worker:
          </p>
          <pre className="mt-2 p-2 rounded bg-slate-900 text-xs text-sky-300 overflow-x-auto">{`docker compose restart worker-za-heavy`}</pre>
        </div>
      </div>

      <p className="text-xs text-slate-500 pt-1">
        Never train only on failed cases — always blend new labels with correct samples to avoid catastrophic forgetting.
      </p>
    </div>
  );
}

// ── Error history table ────────────────────────────────────────────────────

function ErrorTable({ errors }: { errors: HITLError[] }) {
  if (errors.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-600">
        <span className="text-4xl">🎉</span>
        <p className="text-sm">No pipeline errors recorded yet.</p>
      </div>
    );
  }

  // Group counts by stage for summary chips
  const stageCounts: Record<string, number> = {};
  for (const e of errors) stageCounts[e.stage] = (stageCounts[e.stage] ?? 0) + 1;

  return (
    <div className="space-y-3">
      {/* Summary chips */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(stageCounts).map(([stage, count]) => (
          <span key={stage} className={`text-xs px-2 py-0.5 rounded-full border font-medium ${stageColor(stage)}`}>
            {stage.replace(/_/g, " ")}: {count}
          </span>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-lg border border-rdp-border overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-rdp-card/50 text-slate-500 border-b border-rdp-border">
              <th className="text-left px-3 py-2 font-medium">Stage</th>
              <th className="text-left px-3 py-2 font-medium">Reason</th>
              <th className="text-center px-3 py-2 font-medium">Conf.</th>
              <th className="text-center px-3 py-2 font-medium">Outcome</th>
              <th className="text-right px-3 py-2 font-medium">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rdp-border/50">
            {errors.map((e) => (
              <tr key={e.session_id} className="hover:bg-rdp-hover/30 transition-colors">
                <td className="px-3 py-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${stageColor(e.stage)}`}>
                    {e.stage.replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-3 py-2 text-slate-400 font-mono text-[10px] truncate max-w-[180px]">
                  {e.reason}
                </td>
                <td className="px-3 py-2 text-center">{confBadge(e.confidence)}</td>
                <td className="px-3 py-2 text-center">{outcomeBadge(e.outcome)}</td>
                <td className="px-3 py-2 text-right text-slate-500 font-mono">
                  {e.created_at ? new Date(e.created_at).toLocaleDateString() : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

type Tab = "history" | "guide";

export function AITraining() {
  const [tab, setTab] = useState<Tab>("history");
  const [errors, setErrors] = useState<HITLError[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api.models.errorHistory()
      .then(setErrors)
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-3.5 border-b border-rdp-border flex-shrink-0">
        <span className="text-base">🧠</span>
        <div>
          <h2 className="text-sm font-semibold text-white">AI Training</h2>
          <p className="text-[10px] text-slate-500">Active learning & model retraining</p>
        </div>
        <div className="ml-auto flex gap-1">
          {(["history", "guide"] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={[
                "px-3 py-1 rounded-lg text-xs font-medium transition-colors cursor-pointer",
                tab === t ? "bg-rdp-hover text-white" : "text-slate-500 hover:text-slate-300",
              ].join(" ")}
            >
              {t === "history" ? "Error History" : "Retraining Guide"}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5">
        {tab === "history" ? (
          loading ? (
            <div className="flex items-center justify-center py-16 gap-2 text-slate-500 text-sm">
              <div className="w-4 h-4 rounded-full border border-rdp-border border-t-blue-500 animate-spin" />
              Loading error history…
            </div>
          ) : err ? (
            <p className="text-red-400 text-sm">{err}</p>
          ) : (
            <ErrorTable errors={errors} />
          )
        ) : (
          <RetrainingGuide />
        )}
      </div>
    </div>
  );
}
